import React, { useState, useEffect } from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import AccountCircle from '@mui/icons-material/AccountCircle'
import SearchIcon from '@mui/icons-material/Search'
import MenuIcon from '@mui/icons-material/Menu'
import { Link, router, usePage } from '@inertiajs/react'
import { SearchDialog } from '@/components/search/SearchDialog'
import { useAgentNotifications } from '@/hooks/useAgentNotifications'
import { NotificationPermissionBanner } from '@/components/NotificationPermissionBanner'
import { NotificationCenter } from '@/components/NotificationCenter'
import Badge from '@mui/material/Badge'
import { ApprovalBadge } from '@/components/ApprovalBadge'
import { useUnreadMessagesChannel } from '@/hooks/useUnreadMessagesChannel'

interface Props {
  title?: string
  action?: React.ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  noPadding?: boolean
  children: React.ReactNode
}

function AgentNotifications() {
  useAgentNotifications()
  return null
}

const NAV_ITEMS = [
  { label: 'Tasks', href: '/tasks/all' },
  { label: 'Notebooks', href: '/notebooks/notebooks' },
  { label: 'Messages', href: '/messages/channels' },
  { label: 'Manage', href: '/manage/projects' },
  { label: 'Monitoring', href: '/monitoring/session_logs' },
]

const DOCS_ITEMS = [
  { label: 'Auth API', href: '/docs/api/v1/auth' },
  { label: 'Agents API', href: '/docs/api/v1/agents' },
  { label: 'Tasks API', href: '/docs/api/v1/tasks' },
  { label: 'Secrets API', href: '/docs/api/v1/agents/secrets' },
  { label: 'Notebooks API', href: '/docs/api/v1/notebooks' },
  { label: 'Webhooks API', href: '/docs/api/v1/webhooks' },
  { label: 'Messages API', href: '/docs/api/v1/messages' },
  { label: 'Projects API', href: '/docs/api/v1/projects' },
  { label: 'Runners API', href: '/docs/api/v1/agent_runners' },
]

export function AppLayout({ title, action, maxWidth = 'lg', noPadding = false, children }: Props) {
  const { flash, current_agent } = usePage().props as any
  const currentPath = (usePage().url || '').split('?')[0]
  const unreadMessagesCount = useUnreadMessagesChannel()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  function isActive(href: string) {
    if (href === '/') return currentPath === '/'
    return currentPath.startsWith(href)
  }

  function navSx(href: string) {
    return isActive(href)
      ? { color: 'text.primary', fontWeight: 600 }
      : { color: 'text.secondary', fontWeight: 400 }
  }
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [docsAnchorEl, setDocsAnchorEl] = useState<null | HTMLElement>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [noticeOpen, setNoticeOpen] = useState(!!flash?.notice)
  const [alertOpen, setAlertOpen] = useState(!!flash?.alert)

  useEffect(() => { setNoticeOpen(!!flash?.notice) }, [flash?.notice])
  useEffect(() => { setAlertOpen(!!flash?.alert) }, [flash?.alert])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  function handleMenuOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget)
  }

  function handleMenuClose() {
    setAnchorEl(null)
  }

  function handleDocsMenuOpen(event: React.MouseEvent<HTMLElement>) {
    setDocsAnchorEl(event.currentTarget)
  }

  function handleDocsMenuClose() {
    setDocsAnchorEl(null)
  }

  function handleSignOut() {
    handleMenuClose()
    router.delete('/agents/sign_out')
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          {/* Mobile hamburger menu */}
          {isMobile && current_agent && (
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1, color: 'text.primary', minWidth: 44, minHeight: 44 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h3"
            component={Link}
            href="/"
            sx={{
              flexGrow: 0,
              mr: { xs: 'auto', md: 4 },
              color: 'text.primary',
              textDecoration: 'none',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              fontSize: { xs: '1.1rem', md: undefined },
            }}
          >
            Okonomi OS
          </Typography>

          {/* Desktop navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.href}
                  component={Link}
                  href={item.href}
                  sx={navSx(item.href)}
                >
                  {item.label === 'Messages' ? (
                    <Badge
                      badgeContent={unreadMessagesCount}
                      color="primary"
                      max={99}
                      sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}
                    >
                      {item.label}
                    </Badge>
                  ) : (
                    item.label
                  )}
                </Button>
              ))}
              <Button
                onClick={handleDocsMenuOpen}
                sx={{ color: 'text.secondary', fontWeight: 400 }}
              >
                API Docs
              </Button>
              <Menu
                anchorEl={docsAnchorEl}
                open={Boolean(docsAnchorEl)}
                onClose={handleDocsMenuClose}
              >
                {DOCS_ITEMS.map((item) => (
                  <MenuItem
                    key={item.href}
                    component="a"
                    href={item.href}
                    target="_blank"
                    onClick={handleDocsMenuClose}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}

          {current_agent && (
            <IconButton
              onClick={() => setSearchOpen(true)}
              size="small"
              sx={{ color: 'text.secondary', mr: 0.5 }}
            >
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}

          {current_agent && <NotificationCenter />}

          {current_agent?.admin && <ApprovalBadge />}

          {current_agent && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {!isMobile && (
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  {current_agent.username}
                </Typography>
              )}
              <IconButton onClick={handleMenuOpen} size="small">
                <AccountCircle />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  component={Link}
                  href="/agents/edit"
                  onClick={handleMenuClose}
                >
                  Edit profile
                </MenuItem>
                <MenuItem onClick={handleSignOut}>
                  Sign out
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile navigation drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Okonomi OS
          </Typography>
        </Box>
        <Divider />
        <List>
          {NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.href}
              component={Link}
              href={item.href}
              selected={isActive(item.href)}
              onClick={() => setDrawerOpen(false)}
              sx={{
                minHeight: 44,
                '&.Mui-selected': { backgroundColor: 'action.selected' },
              }}
            >
              <ListItemText
                primary={
                  item.label === 'Messages' ? (
                    <Badge
                      badgeContent={unreadMessagesCount}
                      color="primary"
                      max={99}
                      sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}
                    >
                      <span>{item.label}</span>
                    </Badge>
                  ) : (
                    item.label
                  )
                }
                primaryTypographyProps={{
                  fontWeight: isActive(item.href) ? 600 : 400,
                }}
              />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <List>
          <ListItemButton disabled>
            <ListItemText
              primary="API Docs"
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
              }}
            />
          </ListItemButton>
          {DOCS_ITEMS.map((item) => (
            <ListItemButton
              key={item.href}
              component="a"
              href={item.href}
              target="_blank"
              onClick={() => setDrawerOpen(false)}
              sx={{ pl: 4, minHeight: 44 }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      {current_agent && <AgentNotifications />}
      {noPadding ? (
        children
      ) : (
        <Container maxWidth={maxWidth} sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
          {current_agent && <NotificationPermissionBanner />}
          {title && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>{title}</Typography>
              {action}
            </Box>
          )}
          {children}
        </Container>
      )}
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      <Snackbar
        open={noticeOpen && !!flash?.notice}
        autoHideDuration={5000}
        onClose={(_e, reason) => { if (reason !== 'clickaway') setNoticeOpen(false) }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setNoticeOpen(false)} variant="filled">
          {flash?.notice}
        </Alert>
      </Snackbar>
      <Snackbar
        open={alertOpen && !!flash?.alert}
        autoHideDuration={8000}
        onClose={(_e, reason) => { if (reason !== 'clickaway') setAlertOpen(false) }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setAlertOpen(false)} variant="filled">
          {flash?.alert}
        </Alert>
      </Snackbar>
    </Box>
  )
}
